import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { getPrivateImageUrl } from '../../../../config/urls';
import theme from '../../../../styled-components/dark';
import { SportIds, type SportId } from '../../../../types/sports';
import { LineUpFieldsBySportId } from '../../assets/img/index';
import { LineUpFieldBySportId, LineUpInfoBySportId } from '../../constants/ui';
import LineUpPlayerContainer from '../LineUpPlayer/LineUpPlayerContainer';

type WrapperType = {
  $sportId: SportId;
};

const Wrapper = styled.div<WrapperType>`
  position: relative;
  color: white;
  border-radius: 1.5em;

  ${({ $sportId }) => {
    const fieldStyle = LineUpFieldBySportId[$sportId];

    return css`
      padding: ${fieldStyle.wrapperPadding}em;
      ${fieldStyle.wrapperTopPadding
        ? css`
            padding-top: ${fieldStyle.wrapperTopPadding}em;
          `
        : ''}
    `;
  }}

  display: flex;
  justify-content: center;
  align-items: center;
`;

const FieldImg = styled.img`
  width: 100%;
  height: 100%;
  padding-bottom: 1em;
  vertical-align: middle;
  user-select: none;
`;

type FieldAreaType = {
  $sportId: SportId;
};

const FieldArea = styled.div<FieldAreaType>`
  position: absolute;
  background-color: transparent;

  width: 100%;

  ${({ $sportId }) => {
    const fieldStyle = LineUpFieldBySportId[$sportId];

    return css`
      height: calc(100% - ${fieldStyle.sidePadding}em);
      top: ${fieldStyle.topPadding}em;
      transform: translateY(-${fieldStyle.topOffset}em);
      padding: 0 ${fieldStyle.innerPadding}em ${fieldStyle.bottomPadding}em;
    `;
  }}

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

type PlayersRowType = {
  $heightRatio: number;
  $nbPlayers: number;
  $sportId: SportId;
};

const PlayersRow = styled.div<PlayersRowType>`
  display: flex;
  align-items: center;

  ${({ $heightRatio, $nbPlayers, $sportId }) => {
    const fieldStyle = LineUpFieldBySportId[$sportId];

    return css`
      justify-content: ${fieldStyle?.playerRowJustifyContent};
      height: ${$heightRatio * 100}%;
      width: ${$nbPlayers <= 4 ? 25 * $nbPlayers : 100}%;

      ${$heightRatio <= 0.2
        ? css`
            font-size: ${100 - 2 / $heightRatio}%;
          `
        : undefined}
    `;
  }}
`;

type PlayerWrapperType = {
  $widthRatio?: number;
  $offsetValue?: number;
};

const PlayerWrapper = styled.div<PlayerWrapperType>`
  color: white;
  display: flex;
  justify-content: space-around;
  align-items: center;

  ${({ $widthRatio, $offsetValue }) => {
    return css`
      ${typeof $widthRatio === 'number'
        ? css`
            width: ${$widthRatio * 100}%;
          `
        : undefined}

      ${$offsetValue
        ? css`
            transform: translateY(${$offsetValue}%);
          `
        : undefined}
    `;
  }}
`;

type LineUpInfoWrapperType = {
  $sportId: SportId;
};

const LineUpInfoWrapper = styled.div<LineUpInfoWrapperType>`
  position: absolute;
  background-color: ${theme.LineUpField.infoBackgroundColor};
  top: 50%;
  left: 50%;
  padding: 0.3em 0.6em;
  border-radius: 0.6em;

  ${({ $sportId }) => {
    const infoStyle = LineUpInfoBySportId[$sportId];

    return css`
      transform: translate(-50%, calc(-50% - ${infoStyle.yOffset}em));
      max-width: ${infoStyle.maxWidth}em;
    `;
  }}
`;

const LineUpInfoLabel = styled.div`
  font-size: 0.9em;
  line-height: 1.25em;
  text-align: center;
  font-weight: bold;
`;

type LineUpFieldProps = {
  className?: string;
  sportId: SportId;
  goalKeeperPlayerId?: number;
  formedPlayerIds: Array<Array<number>>;
  isMMLoading: boolean;
  fieldImg?: string;
  infoLabel?: string;
};

const LineUpField = ({
  className = '',
  sportId,
  goalKeeperPlayerId,
  formedPlayerIds,
  fieldImg,
  infoLabel,
}: LineUpFieldProps) => {
  const LineUpFieldImage = useMemo(
    () => (fieldImg ? getPrivateImageUrl(fieldImg) : LineUpFieldsBySportId[sportId]),
    [sportId, fieldImg],
  );

  const defaultLineUpFieldImage = useMemo(() => LineUpFieldsBySportId[sportId], [sportId]);

  const computeOffsetValue = useCallback(
    (row: number, playerPosition: number) => {
      if (sportId === SportIds.BASKETBALL) {
        if (row === 1 && playerPosition === 1) {
          return -45;
        }
        if (row === 2 && playerPosition === 1) {
          return 45;
        }
      }
      if (sportId === SportIds.ICEHOCKEY) {
        if (row === 1) {
          return -15;
        }
        if (row === 2) {
          return 15;
        }
      }
      return 0;
    },
    [sportId],
  );

  const computeWidthRatio = useCallback(
    (rowLength: number) => {
      if (sportId === SportIds.BASKETBALL) {
        return 0.33;
      }
      return 1 / rowLength;
    },
    [sportId],
  );

  const computeNbPlayers = useCallback(
    (nbPlayers: number) => {
      if (sportId === SportIds.BASKETBALL) {
        return 4;
      }
      if (sportId === SportIds.ICEHOCKEY) {
        if (nbPlayers === 2) {
          return 2.75;
        }
        return 4;
      }
      return nbPlayers;
    },
    [sportId],
  );

  return LineUpFieldImage ? (
    <Wrapper className={className} $sportId={sportId}>
      <FieldImg
        src={LineUpFieldImage}
        onError={(e) => {
          if (e.target instanceof HTMLImageElement) {
            e.target.src = defaultLineUpFieldImage;
          }
        }}
      />
      {infoLabel ? (
        <LineUpInfoWrapper $sportId={sportId}>
          <LineUpInfoLabel>{infoLabel}</LineUpInfoLabel>
        </LineUpInfoWrapper>
      ) : null}
      <FieldArea $sportId={sportId}>
        {formedPlayerIds.map((playerIdsGroup, j) => {
          return (
            <PlayersRow
              key={`player-row-${j}`}
              $heightRatio={1 / (formedPlayerIds.length + (goalKeeperPlayerId ? 1 : 0))}
              $nbPlayers={computeNbPlayers(playerIdsGroup.length)}
              $sportId={sportId}
            >
              {playerIdsGroup.map((playerId, i) => (
                <PlayerWrapper
                  key={`player-wrapper-${playerId}`}
                  $widthRatio={computeWidthRatio(playerIdsGroup.length)}
                  $offsetValue={computeOffsetValue(j, i)}
                >
                  <LineUpPlayerContainer playerId={playerId} />
                </PlayerWrapper>
              ))}
            </PlayersRow>
          );
        })}
        {goalKeeperPlayerId ? (
          <PlayersRow $heightRatio={1 / (formedPlayerIds.length + 1)} $nbPlayers={1} $sportId={sportId}>
            <PlayerWrapper $widthRatio={1}>
              <LineUpPlayerContainer key={goalKeeperPlayerId} playerId={goalKeeperPlayerId} isGoalKeeper />
            </PlayerWrapper>
          </PlayersRow>
        ) : null}
      </FieldArea>
    </Wrapper>
  ) : null;
};

export default React.memo<LineUpFieldProps>(LineUpField);
